import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { NumberFormatter } from '@mantine/core';

import { usePagination } from 'hook/usePagination';
import { IFilterState, TBookingStatus } from 'types';
import { useHotelBookingData } from 'libs/data-access';
import { IHotelOnlineBookingList } from 'libs/data-access/types';
import { EBadge, EButton, EPagination, ETable, ETooltip } from 'libs/ui';
import { convertToBookingRef, parseLocalDateTime } from 'utils/utility';
import { Info } from 'lucide-react';

interface IProps {
    filterState: IFilterState;
};

export default function HotelBookingListing({ ...props }: IProps) {

    const navigate = useNavigate();
    const location = useLocation();
    const status: TBookingStatus = location.pathname.split("/")[2].toUpperCase() as TBookingStatus;

    const {
        getHotelBookingData
    } = useHotelBookingData();


    const [paginationQueryParams, setPaginationQueryParams] = useState<{
        id: number | null;
        date: string | null;
    }>({
        id: null,
        date: null
    });
    const [sortingDetails, setSortingDetails] = useState<{
        sortBy: "id" | "createdDate";
        sortingOrder: "ASC" | "DESC";
    }>({
        sortBy: "createdDate",
        sortingOrder: "DESC"
    });


    const {
        dataList: hotelBookingList,
        setDataList,
        isLoading,
        setSearchText,
        searchText,
        goToNextPage,
        goToPreviousPage,
        disablePrev,
        disableNext,
        initialLoading,
        setTriggerNextPage,
        setTriggerPreviousPage,
        goToInitialPage
    } = usePagination<IHotelOnlineBookingList>({
        fetchListData: getHotelBookingData,
        maxLimit: 20,
        // sortby: sortingDetails?.sortBy,
        sortingOrder: sortingDetails?.sortingOrder,
        extraQueryParams: {
            dateType: props.filterState?.dateType?.value || null,
            bookedBy: props.filterState?.bookedBy?.value || null,
            pnr: props.filterState?.pnr?.value || null,
            lastName: props.filterState?.lastName?.value || null,
            referenceNo: props.filterState?.referenceNo?.value || null,
            ticketNo: props.filterState?.ticketNo?.value || null,
            bookingStatus: status ? status : props.filterState?.bookingStatus?.value || null,
            startDate: props.filterState?.startDate?.value || null,
            endDate: props.filterState?.endDate?.value || null,
        } as Record<string, string | number | boolean>,
        paginationParams: paginationQueryParams as Record<string, string | number | boolean>,
        dependencies: []
    });


    return (
        <div>
            <ETable
                loading={isLoading}
            >
                <thead>
                    <tr>
                        <th></th>
                        <th>Ref.</th>
                        <th>Ref. No.</th>
                        <th>Booking Date</th>
                        <th>Booked By</th>
                        {/* <th>Email</th>
                        <th>Phone</th> */}
                        <th>Payment Advice</th>
                        <th>Hotel Name</th>
                        {/* <th>Status</th> */}
                        <th className="flex justify-end">Booking Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        hotelBookingList?.map((eachBooking) => (
                            <tr
                                key={eachBooking.id + "_hotel_booking"}
                            >
                                <td>
                                    <EButton
                                        size="xs"
                                        variant="default"
                                        onClick={() => {
                                            navigate(`/online-booking/Hotels/HB/${eachBooking.id}`, {
                                                state: { filterState: props.filterState, eachBooking }
                                            });
                                        }}
                                    >Manage</EButton>
                                </td>
                                <td>{convertToBookingRef(eachBooking.id, "hotel")}</td>
                                <td>{eachBooking?.referenceNo ?? '-'}</td>
                                {/* <td>{moment(eachBooking?.createdDate).format("DD/MM/YYYY HH:mm")}</td> */}
                                <td>{parseLocalDateTime(eachBooking?.createdDate)}</td>
                                <td>
                                    <ETooltip
                                        radius="md"
                                        label={
                                            <div className="bg-gray-800 text-white p-4 rounded-lg shadow-xl max-w-sm">
                                                {/* Header Section */}
                                                <div className="border-b border-gray-600 pb-3 mb-3">
                                                    <div className="font-bold text-base capitalize mb-1">
                                                        {eachBooking?.bookedBy?.toLowerCase().replaceAll("_", " ")}
                                                    </div>
                                                    {eachBooking?.user?.companyName && (
                                                        <div className="text-blue-300 text-sm font-medium">
                                                            {eachBooking?.user?.companyName}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Primary Contact Info */}
                                                <div className="space-y-2 mb-3">
                                                    <div className="text-white text-sm font-semibold">
                                                        {`${eachBooking?.user?.name?.firstName} ${eachBooking?.user?.name?.lastName}`}
                                                    </div>
                                                    <div className="text-gray-300 text-xs">
                                                        📧 {eachBooking?.user?.emailId}
                                                    </div>
                                                    <div className="text-gray-300 text-xs">
                                                        📞 {eachBooking?.user?.phoneNumber || "N/A"}
                                                    </div>
                                                </div>

                                                {/* Address Section */}
                                                {(eachBooking?.user?.address || eachBooking?.user?.city || eachBooking?.user?.pinCode) && (
                                                    <div className="border-t border-gray-600 pt-3">
                                                        <div className="text-gray-400 text-xs font-medium mb-2 uppercase tracking-wide">
                                                            Address
                                                        </div>
                                                        <div className="text-gray-300 text-xs space-y-1">
                                                            {eachBooking?.user?.address && (
                                                                <div>📍 {eachBooking?.user?.address}</div>
                                                            )}
                                                            {(eachBooking?.user?.city || eachBooking?.user?.pinCode) && (
                                                                <div>
                                                                    {eachBooking?.user?.city}
                                                                    {eachBooking?.user?.city && eachBooking?.user?.pinCode && " - "}
                                                                    {eachBooking?.user?.pinCode}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        }
                                        position="bottom"
                                        withArrow
                                        offset={8}
                                    >
                                        <div className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 p-2 rounded-md transition-colors group">
                                            <div className="flex-1">
                                                <div className="capitalize font-medium">
                                                    {eachBooking?.bookedBy?.toLowerCase().replaceAll("_", " ")}
                                                </div>
                                                <div className="text-slate-600 text-sm">
                                                    {eachBooking?.user?.name?.firstName + " " + eachBooking?.user?.name?.lastName}
                                                </div>
                                            </div>
                                            <Info className="w-4 h-4 text-slate-500 group-hover:text-slate-700 transition-colors" />
                                        </div>
                                    </ETooltip>
                                </td>
                                {/* <td>{eachBooking?.email ?? "-"}</td>
                                <td>{eachBooking?.contactNumber ?? '-'}</td> */}
                                <td>{eachBooking?.invoiceNo ?? '-'}</td>
                                <td>{eachBooking?.paymentOrderResponse?.hotel?.name ?? '-'}</td>
                                {/* <td>
                                    {
                                        <EBadge
                                            variant="light"
                                            color={
                                                eachBooking.bookingStatus === "SUCCESS"
                                                    ?
                                                    "green"
                                                    :
                                                    eachBooking.bookingStatus === "PENDING"
                                                        ?
                                                        "yellow"
                                                        :
                                                        "red"
                                            }
                                        >{eachBooking?.bookingStatus}</EBadge>
                                    }
                                </td> */}
                                <td className="text-right font-semibold">
                                    <NumberFormatter
                                        thousandSeparator
                                        prefix="₹ "
                                        value={eachBooking?.paymentOrderResponse?.hotelTotalAmount}
                                    />
                                </td>
                            </tr>
                        ))
                    }
                </tbody>
            </ETable>

            <EPagination
                isLoading={isLoading}
                disablePrev={disablePrev}
                disableNext={disableNext}
                goToNextPage={() => {
                    setPaginationQueryParams({
                        ...paginationQueryParams,
                        date: hotelBookingList[hotelBookingList.length - 1].bookingDate
                    });
                    setTriggerNextPage(true);
                }}
                goToPreviousPage={() => {
                    setPaginationQueryParams({
                        ...paginationQueryParams,
                        date: hotelBookingList[0].bookingDate
                    });

                    setTriggerPreviousPage(true);
                }}
            />
        </div>
    );

};